import React, { useState, useEffect } from 'react';
import { Box, Stack, Button } from '@mui/material';
import { Image as ImageIcon, AutoAwesome } from '@mui/icons-material';
import { Image } from '../../types';
import { getImages, getPublicImages } from '../../services/images';
import { useLang } from '../../contexts/langContext';
import { useBranding } from '../../contexts/BrandingContext';
import { magicButtonProps } from '../common/ai';
import ImageList from './ImageList';
import ImageGenerationDialog from './ImageGenerationDialog';
import ImageUpload from './ImageUpload';
import EmptyState from '../common/EmptyState';

interface ImageManagerProps {
  mode?: 'gallery' | 'picker';
  onImageSelect?: (imageId: string) => void;
  excludeImageIds?: string[];
  includePublic?: boolean;
  highlightImageId?: string | null;
  selectedTagIds?: string[];
  onCountsChange?: (counts: Record<string | 'all', number>) => void;
}

function ImageManager({ 
  mode = 'gallery', 
  onImageSelect, 
  excludeImageIds = [], 
  includePublic = false,
  highlightImageId: externalHighlightId,
  selectedTagIds = [],
  onCountsChange
}: ImageManagerProps) {
  const { features } = useBranding();
  const enableImageGeneration = features?.enableImageGeneration ?? false;
  const { lang } = useLang();
  
  const [images, setImages] = useState<Image[]>([]);
  const [generationDialogOpen, setGenerationDialogOpen] = useState(false);
  const [newlyGeneratedImageId, setNewlyGeneratedImageId] = useState<string | null>(null);

  useEffect(() => {
    fetchImages();
  }, [includePublic, selectedTagIds]);

  // Calculate and report tag counts whenever images change
  useEffect(() => {
    if (onCountsChange) {
      const counts: Record<string | 'all', number> = { all: images.length };
      images.forEach(image => {
        (image.tagIds || []).forEach(tagId => {
          counts[tagId] = (counts[tagId] || 0) + 1;
        });
      });
      onCountsChange(counts);
    }
  }, [images, onCountsChange]);

  const fetchImages = async () => {
    try {
      const userImages = await getImages();
      let allImages = userImages;
      
      if (includePublic) {
        const publicImages = await getPublicImages();
        // Combine and dedupe by _id
        const imageMap = new Map<string, Image>();
        userImages.forEach(img => imageMap.set(img._id, img));
        publicImages.forEach(img => {
          if (!imageMap.has(img._id)) {
            imageMap.set(img._id, img);
          }
        });
        allImages = Array.from(imageMap.values());
      }
      
      // Filter out excluded images
      const tagFiltered = selectedTagIds.length === 0 ? allImages : allImages.filter(img => selectedTagIds.some(tid => img.tagIds?.includes(tid)));
      const filteredImages = tagFiltered.filter(img => !excludeImageIds.includes(img._id));
      setImages(filteredImages);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleGenerateSuccess = ({ url, _id }: { url: string; _id: string }) => {
    setNewlyGeneratedImageId(_id);
    fetchImages();
    // Clear the highlight after 5 seconds
    setTimeout(() => {
      setNewlyGeneratedImageId(null);
    }, 5000);
  };

  const handleImageSelect = (imageId: string) => {
    if (mode === 'picker' && onImageSelect) {
      onImageSelect(imageId);
    }
  };

  const highlightImageId = externalHighlightId || newlyGeneratedImageId;

  return (
    <Box>
      {images.length === 0 ? (
        <Box sx={{ mt: 4 }}>
          <EmptyState
            icon={ImageIcon}
            message={lang === 'en' ? 'No images yet. Generate your first image!' : '还没有图片。生成你的第一张图片！'}
            sx={{ mb: 3 }} />
          <Stack direction="row" spacing={1} justifyContent="center">
            <ImageUpload onUploadSuccess={fetchImages} />
            {enableImageGeneration && (
              <Button
                onClick={() => setGenerationDialogOpen(true)}
                variant="contained"
                startIcon={<AutoAwesome />}
                {...magicButtonProps}
              >
                {lang === 'en' ? 'Generate Image' : '生成图片'}
              </Button>
            )}
          </Stack>
        </Box>
      ) : (
        <>
          <Stack direction="row" spacing={1} sx={{ my: 2 }}>
            <ImageUpload onUploadSuccess={fetchImages} />
            {enableImageGeneration && (
              <Button
                onClick={() => setGenerationDialogOpen(true)}
                variant="outlined"
                startIcon={<AutoAwesome />}
                {...magicButtonProps}
              >
                {lang === 'en' ? 'Generate Image' : '生成图片'}
              </Button>
            )}
          </Stack>
          <ImageList 
            images={images} 
            onImageUpdate={fetchImages} 
            isUserImages={!includePublic} 
            onImageSelect={mode === 'picker' ? handleImageSelect : undefined}
            highlightImageId={highlightImageId}
          />
        </>
      )}
      
      {enableImageGeneration && (
        <ImageGenerationDialog
          open={generationDialogOpen}
          onClose={() => setGenerationDialogOpen(false)}
          onGenerateSuccess={handleGenerateSuccess}
        />
      )}
    </Box>
  );
}

export default ImageManager; 